import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
	RewardInfo,
	RewardType,
	RewardScene,
	RewardProviderProps,
	Reward,
	RewardContextType,
	VoucherDetailInfo,
	VoucherType,
	VoucherQuotaType,
	VoucherInfo,
} from '@/types';
import { oddsBoosterService } from '@/features/voucher/services/odds-booster.service';
import { PromotionOddsBoostInfo } from '@nf/types/account';

const RewardContext = createContext<RewardContextType>({
	lang: 'en-US',
	platform: '',
	hasReward: false,
	enableCoinEvent: false,
	enableCoinEventUpdate: false,
	enableCoinRewardLevel: false,
	canSeeApp: false,
	rewardScene: RewardScene.None,
	setRewardScene: () => {},
	isOpenedOutsideShop: false,
	isValidPromotionVoucherUser: false,
	setIsOpenedOutsideShop: () => {},
	setNotUsedVoucherData: () => {},
	id: 0,
	UserNickName: '',
	currencyName: '',
	siteId: '',
	sabaCoinWallet: null,
	voucherCondition: {
		enableExchange: true,
		enableOddsBooster: true,
		enableRiskFree: true,
		enableNicknameChange: true,
		enableNewRiskFreeBet: true
	},
	fetchRewardInfoThenUpdate: () => {},
	getAvailableVouchers: async () => [],
	getVoucherHistory: async () => [],
	receiveWeeklyAward: async () => {},
	setOddsBoostParticipantsNotify: async () => {},
	onClose: () => {},
	onKafkaSet: () => {},
	onExclusiveKafkaSet: () => {},
	onSwitchView: (view: string) => {}
});

export function RewardProvider({
	fontClassName,
	children,
	lang,
	id,
	UserNickName,
	currencyName,
	siteId,
	voucherCondition,
	platform,
	hasReward,
	imageServerDomain,
	enableCoinEvent,
	enableCoinEventUpdate,
	enableCoinRewardLevel,
	getSabaCoinRewardLevelsInfo,
	cwcBetAmountData,
	sabaCoinWallet,
	promotionOddsBoostInfo,
	enableSingleMatchJackpot,
	showBeginnersPackPopup,
	getImageSource,
	onKafkaSet,
	onStatementOpen,
	addTransaction,
	onTransactSuccess,
	goToGalaxySport,
	goToSport,
	goToParlay,
	goToEuroCup,
	goToStreamer,
	goToGalaxySurvey,
	getRewardInfo,
	getSabaCoinEventInfo,
	redeemCashWithSabaCoin,
	getRewardLabels,
	getHistory,
	updateEventProgress,
	getRankingInfo,
	getVoucherStock,
	purchaseVoucher,
	getAvailableVouchers,
	getVoucherConditionInfo,
	getVoucherHistory,
	getSabaCoinRecord,
	receiveWeeklyAward,
	setOddsBoostParticipantsNotify,
	getNewVersionInfo,
	getCustomerPrizeInfo,
	isPrizeInfoNoticed,
	isValidPromotionVoucherUser,
	onDismissPackPopup,
	onGetPackPopupStatesPromise,
	onGoToWorldCompetition,
	onGoToLeague,
	onStatementPromotionOpen,
	onStatementSabaCoinOpen,
	onOpenRewardNickNameModal,
	onSwitchView,
	onJackpotPrizePageOpen,
	isJackPotPrizeList
}: RewardProviderProps) {
	const [rewardScene, setRewardScene] = useState(RewardScene.None);
	const [labels, setLabels] = useState({});
	const [isInJackPotPrizeList, setIsInJackPotPrizeList] = useState(false);
	const [svgBlobUrl, setSvgBlobUrl] = useState('');
	const [planInfo, setPlanInfo] = useState<RewardInfo>({
		UserName: '',
		VipLevel: NaN,
		IsLevelUpNotification: false,
		CurrentExp: NaN,
		PeriodEndDate: '',
		CanSignIn: false,
		IsVipMax: false,
		WeeklyAward: {
			Awards: []
		},
		VipLevels: [],
		Rewards: [],
		SabaCoin: 0,
		IsJackPotNotice: false,
	});
	const [canSignin, setCanSignin] = useState(false)
	const [missingRewardId, setMissingRewardId] = useState<number>(0);
	const [eventListReward, setEventListReward] = useState<Reward>({
		RewardId: NaN,
		RewardType: NaN,
		RewardName: '',
		RewardDescription: '',
		RewardFullDescription: '',
		StartDate: new Date(),
		EndDate: new Date(),
		MaxEventExp: NaN,
		Events: []
	});
	const [cartVoucher, setCartVoucher] = useState<VoucherDetailInfo>({
		VoucherId: 0,
		VoucherType: VoucherType.OddsBooster,
		Name: '',
		Description: '',
		Price: 0,
		VoucherQuotaType: VoucherQuotaType.Currency,
		VoucherDetailId: 0,
		BaseQuota: 0,
		ActualRate: 0,
		PurchasedCountByUser: 0,
		LimitPerUser: 0,
		LimitPerPeriod: 0,
		QuantitySold: 0,
		CurrencyId: 0,
		QuotaAfterRate: 0,
		CanBuy: false
	});

	const [levelUpFlag, setLevelUpFlag] = useState(false);
	const [purchaseMessage, setPurchaseMessage] = useState<string>();
	const [isOpenedOutsideShop, setIsOpenedOutsideShop] = useState(false);
	const [notUsedVoucherData, setNotUsedVoucherData] = useState<undefined | VoucherInfo>(undefined);
	const isAbortedRef = useRef(false);
	const isNoneScene = rewardScene === RewardScene.None;

	const getInitialScene = ({
		promotionOddsBoostInfo,
		rewardInfo,
		isLevelUp,
		hasNotUsedVoucher
	}: {
		promotionOddsBoostInfo?: PromotionOddsBoostInfo;
		rewardInfo?: RewardInfo;
		isLevelUp: boolean;
		hasNotUsedVoucher: boolean;
	}
	) => {
		if (isLevelUp) {
			return RewardScene.LevelupPopup;
		}
		const weeklyAwards = rewardInfo?.WeeklyAward?.Awards ?? [];

		if (showBeginnersPackPopup) {
			return RewardScene.BeginnersPackPopup;
		}

		if (weeklyAwards.length > 0) {
			return RewardScene.RewardGift;
		}
		
		if (oddsBoosterService.shouldShowOddsBoosterGift(promotionOddsBoostInfo)) {
			return RewardScene.OddsBoosterGift;
		}

		if (hasNotUsedVoucher && promotionOddsBoostInfo && !promotionOddsBoostInfo?.IsNotify) {
			return RewardScene.NotUsed;
		}

		return RewardScene.None;
	};
	
	const updateNotUsedVoucherData = async () => {
		try {
			const vouchers = await getAvailableVouchers();
			if (Array.isArray(vouchers) && vouchers.length > 0) {
				const earliestVoucher = vouchers
					.filter(voucher => voucher.VoucherType === VoucherType.FreeOddsBooster || voucher.VoucherType === VoucherType.AffPromotionOddsBoost) 
					.reduce<VoucherInfo | null>((earliest, current) => {
						if (!earliest) return current;
						const earliestDate = new Date(earliest.ExpireDate);
						const currentDate = new Date(current.ExpireDate);
						return earliestDate && currentDate && currentDate < earliestDate ? current : earliest;
					}, null);
				if (earliestVoucher && oddsBoosterService.hasOddsBoosterPromotion(promotionOddsBoostInfo)) {
					setNotUsedVoucherData(earliestVoucher);
					return earliestVoucher;
				}
			}
		} catch (error) {
			console.log(error);
		}
	}

	const fetchRewardInfoThenUpdate = async () => {
		try {
			const rewardInfo = await getRewardInfo();
			if (rewardInfo) {
				const isLevelUp = levelUpFlag || rewardInfo.IsLevelUpNotification;
				setPlanInfo({
					...rewardInfo,
					IsLevelUpNotification: isLevelUp,
					IsJackPotNotice: rewardInfo.IsJackPotNotice ?? false,
					VipLevels: [...rewardInfo.VipLevels],
				});
	
				if (isNoneScene) {
					const notUsedVoucher = await updateNotUsedVoucherData();
					const newScene = getInitialScene({ promotionOddsBoostInfo, rewardInfo, isLevelUp, hasNotUsedVoucher: !!notUsedVoucher });   
					setRewardScene(newScene);
					setCanSignin(rewardInfo.CanSignIn);
					isAbortedRef.current = newScene === RewardScene.None;
				}
			}
		} catch (error) {
			console.log(error);
		}
	}

	const handleNotRewardUser = async () => {
		try {
			const notUsedVoucher = await updateNotUsedVoucherData();
			if (oddsBoosterService.shouldShowOddsBoosterGift(promotionOddsBoostInfo)) {
				setRewardScene(RewardScene.OddsBoosterGift);
			} else if (notUsedVoucher && promotionOddsBoostInfo && !promotionOddsBoostInfo?.IsNotify) {
				setRewardScene(RewardScene.NotUsed);
			}
		} catch (error) {
			console.log(error);
		}
	}

	const resetIsAborted = () => {
		if (isAbortedRef.current) {
			isAbortedRef.current = false;
		}
	};

	useEffect(() => {
		if (!hasReward) {
			// Users without rewards confirm odds boost gifts
			handleNotRewardUser();
			return;
		};

		const updater = setInterval(() => {
			fetchRewardInfoThenUpdate();
		}, 5 * 60 * 1000);

		return () => clearInterval(updater);
	}, [hasReward, showBeginnersPackPopup]);

	useEffect(() => {
		const currentRewards = planInfo.Rewards;
		if (missingRewardId && Array.isArray(currentRewards) && currentRewards.length > 0) {
			const missingRewardIndex = currentRewards.findIndex(reward => reward.RewardId === missingRewardId);
			if (missingRewardIndex !== -1) {
				setMissingRewardId(0);
				setEventListReward(currentRewards[missingRewardIndex]);
			}
		}
	}, [missingRewardId, planInfo.Rewards]);

	useEffect(() => {
		if (!isAbortedRef.current) {
			fetchRewardInfoThenUpdate();
		}
	}, [isNoneScene]);

	useEffect(() => {
		if (!isNoneScene) {
			getRewardLabels().then(labels => {
				setLabels(labels);
			});
		}
	}, [isNoneScene]);

	useEffect(() => {
		if (enableSingleMatchJackpot && planInfo.IsJackPotNotice && ['Mars', 'Galaxy'].includes(platform ?? '')) {
			resetIsAborted();
			setRewardScene(RewardScene.JackPotNotice);
		}
	}, [planInfo.IsJackPotNotice]);

	useEffect(() => {
		if (planInfo.IsLevelUpNotification) {
			setRewardScene(RewardScene.LevelupPopup);
		}
	}, [planInfo.IsLevelUpNotification]);

	useEffect(() => {
		if (purchaseMessage) {
			setTimeout(() => {
				setPurchaseMessage('');
			}, 3000);
		}
	}, [purchaseMessage]);

	useEffect(() => {
		if (!isAbortedRef.current && hasReward && enableSingleMatchJackpot && isJackPotPrizeList) {
			isJackPotPrizeList().then(result => {
				setIsInJackPotPrizeList(result);
			});
		}
	}, [isNoneScene]);

	useEffect(() => {
		const openReward = () => {
			resetIsAborted();
			setRewardScene(RewardScene.Home);
			setIsOpenedOutsideShop(false);
		};

		const openLevelup = () => {
			openReward();
			setRewardScene(RewardScene.LevelupPopup);
		};

		const openSignIn = () => {
			openReward();
			setRewardScene(RewardScene.SignInSpin);
		}

		const openSabaShop = () => {
			resetIsAborted();
			setRewardScene(RewardScene.Shop);
			setIsOpenedOutsideShop(false);
		}

		const openVouchers = () => {
			resetIsAborted();
			setRewardScene(RewardScene.MyVouchers);
			setIsOpenedOutsideShop(true);
		};

		const openNewVersion = () => {
			openReward();
			setRewardScene(RewardScene.NewVersion);
		}

		const openUm = () => {
			openReward();
			setRewardScene(RewardScene.UM);
		}

		const openJackPotNotice = () => {
			openReward();
			setRewardScene(RewardScene.JackPotNotice);
		}

		const openEventList = () => {
			openReward();
			setEventListScene(8);
		};

		document.addEventListener('open-newversion', openNewVersion, false);
		document.addEventListener('open-signin', openSignIn, false);
		document.addEventListener('open-saba-shop', openSabaShop, false);
		document.addEventListener('open-vouchers', openVouchers, false);
		document.addEventListener('open-reward', openReward, false);
		document.addEventListener('open-levelup', openLevelup, false);
		document.addEventListener('open-um', openUm, false);
		document.addEventListener('open-jackpotnotice', openJackPotNotice, false);
		document.addEventListener('open-booked-bet-reward', openEventList, false);
		
		return () => {
			document.removeEventListener('open-newversion', openNewVersion, false);
			document.removeEventListener('open-signin', openSignIn, false);
			document.removeEventListener('open-saba-shop', openSabaShop, false);
			document.removeEventListener('open-vouchers', openVouchers, false);
			document.removeEventListener('open-reward', openReward, false);
			document.removeEventListener('open-levelup', openLevelup, false);
			document.removeEventListener('open-um', openUm, false);
			document.removeEventListener('open-jackpotnotice', openJackPotNotice, false);
			document.removeEventListener('open-booked-bet-reward', openEventList, false);
		};
	}, [isNoneScene]);

	const setEventListScene = (rewardId: number) => {
		const eventListRewardsOfId: Reward[] = planInfo.Rewards.filter(data => data.RewardId === rewardId);
		if (eventListRewardsOfId.length > 0) {
			setEventListReward(eventListRewardsOfId[0]);
		} else if (Number.isNaN(eventListReward.RewardId)) {
			setMissingRewardId(rewardId);
		}
		setRewardScene(RewardScene.EventList);
	};
	const openVoucherConfirm = (voucherDetailInfo: VoucherDetailInfo) => {
		setCartVoucher(voucherDetailInfo);
		setRewardScene(RewardScene.PurchaseConfirm);
	};
	const openRfbInfo = () => {
		setRewardScene(RewardScene.RfbInfo);
	};
	const onClose = () => {
		setRewardScene(RewardScene.None);
		isAbortedRef.current = true;
	};
	const goToEvent = (rewardType: number, rewardId: number) => {
		switch (rewardType) {
			case RewardType.Betting:
				onClose();
				rewardId === 6 && !['Mars', 'Galaxy'].includes(platform ?? '') ? goToGalaxySport() : goToSport();
				break;
			case RewardType.Parlay:
				onClose();
				goToParlay();
				break;
			case RewardType.Streamer:
				onClose();
				goToStreamer();
				break;
			case RewardType.Survey:
				onClose();
				goToGalaxySurvey();
				break;
			case RewardType.BookedBet:
				onClose();
				if (typeof goToEuroCup === 'function') {
					goToEuroCup();
				}
				break;
			default:
				break;
		}
	}

	const sceneValue = useMemo(() => ({ rewardScene, setRewardScene }), [rewardScene]);
	const resource = useMemo(() => ({ labels, setLabels }), [labels]);
	const planInfoValue = useMemo(() => ({ planInfo, setPlanInfo }), [planInfo]);
	const getSabaCoinEventInfoFn = useMemo(() => getSabaCoinEventInfo, []);
	const isOpenedOutsideShopValue = useMemo(() => ({ isOpenedOutsideShop, setIsOpenedOutsideShop }), [isOpenedOutsideShop]);

	const canSeeApp = !isNoneScene && !isAbortedRef.current;

	const onExclusiveKafkaSet = ({ type, action }: { type: number, action: number }) => {
		onKafkaSet({
			note: 'SABA_Exclusive_Rewards',
			type,
			action,
		});
	}

	const value: RewardContextType = {
		fontClassName,
		lang,
		hasReward,
		canSeeApp,
		voucherCondition,
		cwcBetAmountData,
		sabaCoinWallet,
		promotionOddsBoostInfo,
		...sceneValue,
		...resource,
		...planInfoValue,
		...isOpenedOutsideShopValue,
		canSignin,
		eventListReward,
		cartVoucher,
		id,
		UserNickName,
		currencyName,
		siteId,
		imageServerDomain,
		enableCoinEvent: Boolean(enableCoinEvent),
		enableCoinEventUpdate: Boolean(enableCoinEventUpdate),
		enableCoinRewardLevel: Boolean(enableCoinRewardLevel),
		notUsedVoucherData,
		setNotUsedVoucherData,
		getSabaCoinRewardLevelsInfo,
		isInJackPotPrizeList: isInJackPotPrizeList,
		getImageSource,
		addTransaction,
		onTransactSuccess,
		onKafkaSet,
		onExclusiveKafkaSet,
		platform,
		onStatementOpen,
		setPlanInfo,
		goToEvent,
		setEventListScene,
		openVoucherConfirm,
		openRfbInfo,
		onClose,
		enablePromoteBookedBet: (typeof goToEuroCup === 'function'),
		setCanSignin,
		getHistory,
		updateEventProgress,
		getRankingInfo,
		getVoucherStock,
		purchaseVoucher,
		getAvailableVouchers,
		getVoucherConditionInfo,
		getVoucherHistory,
		getSabaCoinEventInfo: getSabaCoinEventInfoFn,
		redeemCashWithSabaCoin,
		getSabaCoinRecord,
		receiveWeeklyAward,
		setOddsBoostParticipantsNotify,
		fetchRewardInfoThenUpdate,
		getNewVersionInfo,
		purchaseMessage,
		setPurchaseMessage,
		svgBlobUrl,
		setSvgBlobUrl,
		getCustomerPrizeInfo,
		isPrizeInfoNoticed,
		isValidPromotionVoucherUser,
		onDismissPackPopup,
		onGetPackPopupStatesPromise,
		onGoToWorldCompetition,
		onGoToLeague,
		onStatementPromotionOpen,
		onStatementSabaCoinOpen,
		onOpenRewardNickNameModal,
		onSwitchView,
		onJackpotPrizePageOpen,
	};

	return (
		<RewardContext.Provider value={value}>
			{children}
		</RewardContext.Provider>
	);
}

RewardContext.displayName = 'reward-context';

export const useReward = (): RewardContextType => useContext(RewardContext);
export const triggerRewardSystemPanel = () => {
	document.dispatchEvent(new CustomEvent('open-reward', {}));
}
